import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { LucideAngularModule, ClipboardCheck, Plus } from 'lucide-angular';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Vendor } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { VendorsService } from '../../vendor/services/vendors.service';

interface OutstandingInvoiceRow {
  id: string;
  invoiceNo: string;
  invoiceDate: string;
  issuedTo: string;
  invoiceAmount: number;
  paidAmount: number;
}

interface ReceivedAmountRow {
  id: string;
  paymentRefNo: string;
  paymentDate: string;
  receivedFrom: string;
  receivedAmount: number;
  settledAmount: number;
}

interface GeneratedInvoiceApi {
  row_key: string;
  bill_id: string;
  mode?: string | null;
  invoice_type?: string | null;
  bill_for?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  rebate_date?: string | null;
  rebate_percentage?: number | null;
  row_json?: Record<string, unknown> | null;
}

interface PaymentFormState {
  clientName: string;
  amount: string;
  transactionDate: string;
  paymentMode: string;
  referenceNumber: string;
  fromBank: string;
  fromIfsc: string;
  toBank: string;
  toIfsc: string;
}

@Component({
  standalone: true,
  selector: 'app-payment-knock-off-page',
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './payment-knock-off.page.html',
  styleUrl: './payment-knock-off.page.scss',
})
export class PaymentKnockOffPage implements OnInit {
  private readonly api = inject(ApiService);
  private readonly vendorsService = inject(VendorsService);

  protected readonly ClipboardCheckIcon = ClipboardCheck;
  protected readonly PlusIcon = Plus;

  protected readonly selectedClient = signal('');
  protected readonly fromDate = signal('');
  protected readonly toDate = signal('');
  protected readonly receivedFromDate = signal('');
  protected readonly receivedToDate = signal('');
  protected readonly invoiceSearch = signal('');
  protected readonly payerSearch = signal('');
  protected readonly showPaymentModal = signal(false);
  protected readonly paymentModes = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque'];
  protected readonly banks = ['HDFC Bank', 'ICICI Bank', 'State Bank of India', 'Axis Bank', 'Kotak Mahindra Bank'];

  protected readonly clients = signal<string[]>([]);

  private readonly outstandingInvoices = signal<OutstandingInvoiceRow[]>([]);

  protected readonly receivedAmounts = signal<ReceivedAmountRow[]>([
    {
      id: 'ra-1',
      paymentRefNo: '000303101095724021781',
      paymentDate: '04-05-2025',
      receivedFrom: 'Demo Client',
      receivedAmount: 748512.45,
      settledAmount: 42200,
    },
    {
      id: 'ra-2',
      paymentRefNo: '000303100109572402178',
      paymentDate: '04-05-2025',
      receivedFrom: 'Demo Client',
      receivedAmount: 512455,
      settledAmount: 155169,
    },
    {
      id: 'ra-3',
      paymentRefNo: '000303100109572403178',
      paymentDate: '04-05-2025',
      receivedFrom: 'Demo Client',
      receivedAmount: 248512,
      settledAmount: 0,
    },
  ]);

  protected readonly paymentForm = signal<PaymentFormState>({
    clientName: '',
    amount: '',
    transactionDate: '',
    paymentMode: '',
    referenceNumber: '',
    fromBank: '',
    fromIfsc: '',
    toBank: '',
    toIfsc: '',
  });

  ngOnInit(): void {
    forkJoin({
      vendors: this.vendorsService.list().pipe(catchError(() => of([] as Vendor[]))),
      generated: this.api.get<GeneratedInvoiceApi[]>('/api/invoices/generated').pipe(catchError(() => of([] as GeneratedInvoiceApi[]))),
    }).subscribe(({ vendors, generated }) => {
      const approvedClients = this.extractApprovedClientNames(vendors || []);
      const outstanding = this.mapGeneratedInvoicesToOutstanding(generated || []);
      this.outstandingInvoices.set(outstanding);

      const invoiceClients = Array.from(
        new Set(
          outstanding
            .map((item) => item.issuedTo)
            .filter((name) => !!name && name !== '-'),
        ),
      );
      const allClients = Array.from(new Set([...approvedClients, ...invoiceClients]));
      this.clients.set(allClients);

      const current = this.selectedClient();
      const nextSelected = current && allClients.includes(current) ? current : allClients[0] || '';
      this.selectedClient.set(nextSelected);
    });
  }

  protected readonly filteredOutstanding = computed(() => {
    const q = this.invoiceSearch().trim().toLowerCase();
    const selected = this.selectedClient().trim().toLowerCase();
    const from = this.fromDate();
    const to = this.toDate();

    return this.outstandingInvoices().filter((row) => {
      if (selected && row.issuedTo.toLowerCase() !== selected) return false;
      if (q && !row.invoiceNo.toLowerCase().includes(q) && !row.issuedTo.toLowerCase().includes(q)) return false;
      return this.isWithinDateRange(row.invoiceDate, from, to);
    });
  });

  protected readonly filteredReceived = computed(() => {
    const q = this.payerSearch().trim().toLowerCase();
    const selected = this.selectedClient().trim().toLowerCase();
    const from = this.receivedFromDate();
    const to = this.receivedToDate();

    return this.receivedAmounts().filter((row) => {
      if (selected && row.receivedFrom.toLowerCase() !== selected) return false;
      if (q && !row.receivedFrom.toLowerCase().includes(q) && !row.paymentRefNo.toLowerCase().includes(q)) return false;
      return this.isWithinDateRange(row.paymentDate, from, to);
    });
  });

  protected readonly pendingInvoiceCount = computed(() => this.filteredOutstanding().length);
  protected readonly totalInvoiceAmount = computed(() =>
    this.filteredOutstanding().reduce((sum, row) => sum + row.invoiceAmount, 0),
  );
  protected readonly totalUnpaidAmount = computed(() =>
    this.filteredOutstanding().reduce((sum, row) => sum + Math.max(row.invoiceAmount - row.paidAmount, 0), 0),
  );
  protected readonly totalReceivedAmount = computed(() =>
    this.filteredReceived().reduce((sum, row) => sum + row.receivedAmount, 0),
  );
  protected readonly totalUnsettledAmount = computed(() =>
    this.filteredReceived().reduce((sum, row) => sum + Math.max(row.receivedAmount - row.settledAmount, 0), 0),
  );

  protected readonly amountLeft = computed(
    () => this.totalReceivedAmount() - Math.min(this.totalReceivedAmount(), this.totalInvoiceAmount()),
  );

  protected onClientChange(clientName: string): void {
    this.selectedClient.set((clientName || '').toString());
    this.paymentForm.update((form) => ({
      ...form,
      clientName: (clientName || '').toString(),
    }));
  }

  protected openPaymentModal(): void {
    this.paymentForm.set({
      clientName: this.selectedClient(),
      amount: '',
      transactionDate: '',
      paymentMode: '',
      referenceNumber: '',
      fromBank: '',
      fromIfsc: '',
      toBank: '',
      toIfsc: '',
    });
    this.showPaymentModal.set(true);
  }

  protected closePaymentModal(): void {
    this.showPaymentModal.set(false);
  }

  protected updatePaymentField(field: keyof PaymentFormState, value: string): void {
    this.paymentForm.update((state) => ({ ...state, [field]: value ?? '' }));
  }

  protected savePayment(): void {
    const form = this.paymentForm();
    const amount = Number(form.amount || 0);
    if (!form.clientName || amount <= 0) return;

    const paymentDate = this.normalizeDateForDisplay(form.transactionDate) || this.toDisplayDate(new Date());
    const newRow: ReceivedAmountRow = {
      id: `ra-${Date.now()}`,
      paymentRefNo: form.referenceNumber.trim() || '-',
      paymentDate,
      receivedFrom: form.clientName,
      receivedAmount: amount,
      settledAmount: 0,
    };
    this.receivedAmounts.update((rows) => [newRow, ...rows]);
    this.closePaymentModal();
  }

  protected inr(value: number): string {
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
  }

  private extractApprovedClientNames(vendors: Vendor[]): string[] {
    return Array.from(
      new Set(
        vendors
          .filter((vendor) => this.isVendorApproved(vendor))
          .map((vendor) => (vendor.vendor_name || '').trim())
          .filter((name) => !!name),
      ),
    );
  }

  private isVendorApproved(vendor: Vendor): boolean {
    const status = (vendor.status || '').toLowerCase().trim();
    return status === 'approved' || status === 'active' || status.endsWith('_approved');
  }

  private mapGeneratedInvoicesToOutstanding(rows: GeneratedInvoiceApi[]): OutstandingInvoiceRow[] {
    return rows.map((row, index) => {
      const payload = row.row_json || {};
      const invoiceAmount = Number(payload['net_payable'] ?? payload['amount'] ?? 0) || 0;
      return {
        id: row.row_key || `oi-${index + 1}`,
        invoiceNo: row.bill_id || row.row_key || `INV-${index + 1}`,
        invoiceDate: this.resolveInvoiceDate(payload),
        issuedTo: this.readString(payload['buyer']) || '-',
        invoiceAmount,
        paidAmount: 0,
      };
    });
  }

  private resolveInvoiceDate(payload: Record<string, unknown>): string {
    const directDate = this.readString(payload['invoice_date']);
    if (directDate) return this.normalizeDateForDisplay(directDate) || '-';

    const period = this.readString(payload['period']);
    if (!period) return '-';
    const start = period.split('to')[0]?.trim() || period.split('-')[0]?.trim() || '';
    return this.normalizeDateForDisplay(start) || '-';
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private isWithinDateRange(value: string, fromDate: string, toDate: string): boolean {
    if (!fromDate && !toDate) return true;
    const rowDate = this.parseDate(value);
    if (!rowDate) return true;
    const from = this.parseDate(fromDate);
    const to = this.parseDate(toDate);
    if (from && rowDate < from) return false;
    if (to && rowDate > to) return false;
    return true;
  }

  private normalizeDateForDisplay(value: string): string | null {
    const parsed = this.parseDate(value);
    return parsed ? this.toDisplayDate(parsed) : null;
  }

  private toDisplayDate(date: Date): string {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  private parseDate(value: string): Date | null {
    if (!value) return null;
    const normalized = value.trim();
    if (!normalized) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      const date = new Date(`${normalized}T00:00:00`);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const match = normalized.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (match) {
      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
